import { z } from "zod";

/**
 * The caller's answers to a screening mammogram's screening questions: reported facts, not clinical history or a
 * policy override. Omitted means the question wasn't asked; false and true are explicit answers.
 */
export const callerReportedMammogramScreenSchema = z.strictObject({
  /** A new breast lump, breast pain, nipple thickening or discharge, or a change in breast size or shape. */
  breastSymptoms: z.boolean().optional(),
  /** A follow-up, callback or recheck after an earlier mammogram, or a diagnostic one a provider asked for. */
  followUpImaging: z.boolean().optional(),
  /** Breast implants, or a wheelchair at the visit. */
  implantsOrWheelchair: z.boolean().optional(),
});
export type CallerReportedMammogramScreen = z.infer<typeof callerReportedMammogramScreenSchema>;

/** What the caller could say about when the requested visit's previous one was. */
export enum CallerReportedPreviousVisitKind {
  Date = "DATE",
  /** Only the month and year. */
  Month = "MONTH",
  /** Only that it was longer ago than the visit's required gap, such as more than a year for a yearly screening. */
  OutsideSpacingWindow = "OUTSIDE_SPACING_WINDOW",
  /** Asked, and the caller can't say when. */
  Unknown = "UNKNOWN",
  /** The caller has never had one. */
  None = "NONE",
}

const previousVisitFields = { appointmentTypeChoiceId: z.string().trim().min(1) };

/**
 * The caller's report of when their previous visit of the requested kind was, such as a screening mammogram done
 * elsewhere that the chart doesn't show: reported history, not a verified visit. It names the visit by its choice ID.
 */
export const callerReportedPreviousVisitSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    ...previousVisitFields,
    kind: z.literal(`${CallerReportedPreviousVisitKind.Date}`),
    date: z.iso.date(),
  }),
  z.strictObject({
    ...previousVisitFields,
    kind: z.literal(`${CallerReportedPreviousVisitKind.Month}`),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  }),
  z.strictObject({
    ...previousVisitFields,
    kind: z.literal(`${CallerReportedPreviousVisitKind.OutsideSpacingWindow}`),
  }),
  z.strictObject({ ...previousVisitFields, kind: z.literal(`${CallerReportedPreviousVisitKind.Unknown}`) }),
  z.strictObject({ ...previousVisitFields, kind: z.literal(`${CallerReportedPreviousVisitKind.None}`) }),
]);
export type CallerReportedPreviousVisit = z.infer<typeof callerReportedPreviousVisitSchema>;
